import { useEffect, useState } from "react";
import { X, Download, ExternalLink, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * In-app PDF viewer modal optimized for mobile (especially iOS Safari).
 *
 * Why this exists: opening the PDF in a new tab (window.open) on mobile
 * navigates the user away from the admin panel with no clear way back
 * and the browser's inline PDF view often hides the download button.
 * On iOS Safari the `<a download>` attribute is silently ignored and
 * `Content-Disposition: attachment` opens inline anyway — so we must
 * fetch the file into JS and trigger the native share sheet (which
 * lets the user pick «Guardar em Ficheiros», AirDrop, Mail, etc.).
 *
 * Download strategy (in order):
 *   1. Web Share API with files → iOS share sheet. Best UX on iPhone.
 *   2. Blob URL + <a download> → works on desktop Chrome/Firefox/Edge.
 *   3. As a last resort, window.open the inline URL so the user can
 *      use the browser's native «Save» menu manually.
 */
export default function PdfViewerModal({
  open,
  onClose,
  title = "Fatura-Recibo",
  inlineUrl,
  downloadUrl,
  filename = "factura-recibo.pdf",
}) {
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) setLoading(true);
  }, [open, inlineUrl]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while the modal is open (mobile UX)
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    try {
      // Fetch the PDF as a blob (cookies for auth come along automatically
      // because the URL is same-origin via REACT_APP_BACKEND_URL).
      const resp = await api.get(downloadUrl.replace(api.defaults.baseURL, ""), {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const file = new File([blob], filename, { type: "application/pdf" });

      // 1) Web Share API with files — iOS Safari 15+, Chrome Android.
      //    Opens the OS share sheet so the user can «Guardar em Ficheiros».
      if (
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({ files: [file], title: filename });
          return;
        } catch (err) {
          // AbortError = user cancelled, NotAllowedError = needs gesture.
          // In either case, fall through to the blob-download fallback.
          if (err && err.name === "AbortError") return;
        }
      }

      // 2) Blob URL + <a download> — works on desktop browsers.
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch (e) {
      console.error("PDF download failed", e);
      toast.error("Não foi possível descarregar o PDF. Tente «Abrir em separador» e use o menu do browser.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="pdf-viewer-modal"
    >
      {/* Header — sticky, large hit targets for mobile */}
      <div className="flex items-center justify-between gap-3 px-3 sm:px-5 py-3 bg-[#031411] border-b border-[#1a4640]">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={18} className="text-[#d4af37] shrink-0" />
          <div className="truncate text-sm sm:text-base font-semibold text-[#fdfbf7]">
            {title}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/15 active:bg-white/20 px-3 py-2 text-sm text-white"
          data-testid="pdf-viewer-close"
          aria-label="Fechar"
        >
          <X size={18} /> <span className="hidden sm:inline">Fechar</span>
        </button>
      </div>

      {/* PDF area */}
      <div className="flex-1 min-h-0 relative bg-[#0a1f1c]">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-emerald-100/80 z-10 pointer-events-none">
            <Loader2 size={28} className="animate-spin" />
          </div>
        )}
        <iframe
          key={inlineUrl}
          title={title}
          src={inlineUrl}
          onLoad={() => setLoading(false)}
          className="absolute inset-0 w-full h-full bg-white"
          data-testid="pdf-viewer-iframe"
        />
      </div>

      {/* Footer action bar — always visible, big buttons */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 px-3 sm:px-5 py-3 bg-[#031411] border-t border-[#1a4640]">
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#d4af37] hover:bg-[#f5d76e] active:bg-[#bd991f] disabled:opacity-60 text-[#031411] font-semibold px-4 py-3 text-sm"
          data-testid="pdf-viewer-download"
        >
          {downloading ? (
            <><Loader2 size={18} className="animate-spin" /> A preparar…</>
          ) : (
            <><Download size={18} /> Descarregar PDF</>
          )}
        </button>
        <a
          href={inlineUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-300/30 bg-emerald-300/5 hover:bg-emerald-300/10 active:bg-emerald-300/15 text-emerald-100 font-semibold px-4 py-3 text-sm"
          data-testid="pdf-viewer-open-tab"
        >
          <ExternalLink size={18} /> Abrir em separador
        </a>
      </div>
    </div>
  );
}

/**
 * Convenience hook to centralise the inline/download URL conventions.
 * Pass the booking_id (for issued booking invoices) OR the invoice_id
 * (for manual invoices) and it returns the matching backend URLs.
 */
export function buildInvoicePdfUrls({ bookingId, manualInvoiceId, number }) {
  const base = api.defaults.baseURL;
  if (bookingId) {
    return {
      inlineUrl: `${base}/admin/facturalusa/invoices/${bookingId}/pdf?inline=true`,
      downloadUrl: `${base}/admin/facturalusa/invoices/${bookingId}/pdf`,
      filename: `${number || "factura-recibo"}.pdf`,
    };
  }
  if (manualInvoiceId) {
    return {
      inlineUrl: `${base}/admin/facturalusa/manual/${manualInvoiceId}/pdf?inline=true`,
      downloadUrl: `${base}/admin/facturalusa/manual/${manualInvoiceId}/pdf`,
      filename: `${number || "factura"}.pdf`,
    };
  }
  return { inlineUrl: "", downloadUrl: "", filename: "" };
}
